import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { Song } from '../data/mockCatalog';
import type { PlayRecord, NoticeItem } from '../data/constants';
import type { CognitiveActivity } from '../data/cognitive';
import { INITIAL_RECORDS } from '../data/constants';
import type { GameResult } from '../data/performanceAnalysis';

export type Difficulty = 'Easy' | 'Normal' | 'Hard';
export type ActivePlan = 'free' | 'drummer' | 'cognitive' | 'plus';
export type PurchaseState = 'idle' | 'checking' | 'processing' | 'success' | 'cancelled' | 'failed' | 'restored';
export type PreparationStatus = 'loading' | 'ready' | 'audio-error' | 'abc-error' | 'offline' | 'update-required';

export interface DrumUserProfile {
  memberId?: number;
  name: string;
  email: string;
  picture: string;
  joinedAt?: string;
  grade?: ActivePlan;
}

export interface DrumUserPreferences {
  language: string;
  autoPreview: boolean;
  judgmentSound: boolean;
  notifyNewSongs: boolean;
  notifyNotices: boolean;
  notifyCognitive: boolean;
  fromStore?: boolean;
}

export const DEFAULT_DRUM_PREFERENCES: DrumUserPreferences = {
  language: 'en',
  autoPreview: true,
  judgmentSound: true,
  notifyNewSongs: true,
  notifyNotices: true,
  notifyCognitive: true,
  fromStore: false,
};

export const drumLoggedInAtom = atomWithStorage<boolean>('drum-logged-in', false, createJSONStorage(() => window.localStorage));

export const drumUserTokenAtom = atomWithStorage<string>('drum-user-token', '', createJSONStorage(() => window.localStorage));

export const drumUserProfileAtom = atomWithStorage<DrumUserProfile | null>('drum-user-profile', null, createJSONStorage(() => window.localStorage));

export const drumLanguageAtom = atomWithStorage<string>('drumtale-language', 'en', createJSONStorage(() => window.localStorage));

export const drumSelectedSongAtom = atom<Song | null>(null);

export const drumDifficultyAtom = atom<Difficulty>('Easy');

export const drumGameResultAtom = atom<GameResult | null>(null);

export const drumRecordsAtom = atom<PlayRecord[]>(INITIAL_RECORDS);

export const drumActivePlanAtom = atom<ActivePlan>('free');

export const drumPurchaseTargetAtom = atom<{ song: Song; difficulty: Difficulty } | null>(null);

export const drumPurchaseStateAtom = atom<PurchaseState>('idle');

export const drumPreparationStatusAtom = atom<PreparationStatus>('loading');

export const drumUserPreferencesAtom = atomWithStorage<DrumUserPreferences>('drum-user-preferences', DEFAULT_DRUM_PREFERENCES, createJSONStorage(() => window.localStorage));

export const drumSelectedNoticeAtom = atom<NoticeItem | null>(null);

export const drumSelectedCognitiveAtom = atom<CognitiveActivity | null>(null);

export const drumHasDrumPassAtom = atom((get) => {
  const plan = get(drumActivePlanAtom);
  return plan === 'drummer' || plan === 'plus';
});
